import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginForm from '../components/LoginForm';
import { Icon } from '../components/icons';
import { friendlyProviderError } from '../data/socialProviderStatus';

export default function LoginPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { socialSignIn } = useAuth();
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialError, setSocialError] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code) return;

    // Determine provider by state prefix (naver has no prefix, default)
    const isKakao = state?.startsWith('kakao_');
    const isGoogle = state?.startsWith('google_');
    const stateKey = isKakao ? 'kakao_state' : isGoogle ? 'google_state' : 'naver_state';
    const savedState = sessionStorage.getItem(stateKey);
    const provider = isKakao ? 'kakao' : isGoogle ? 'google' : 'naver';

    if (state !== savedState) {
      setSocialError('인증 상태가 일치하지 않습니다. 다시 시도해주세요.');
      setSearchParams({});
      return;
    }

    sessionStorage.removeItem(stateKey);
    setSocialLoading(true);
    setSearchParams({});

    socialSignIn(provider, {
      code,
      redirectUri: `${window.location.origin}/login`,
    })
      .then(() => {
        try { localStorage.setItem('lastProvider', provider); } catch {}
        window.location.href = '/';
      })
      .catch((err) => {
        setSocialError(friendlyProviderError(provider, err.message));
        setSocialLoading(false);
      });
  }, [searchParams, setSearchParams, socialSignIn]);

  return (
    <div>
      <title>로그인 — KOA DETAILING</title>
      <meta name="robots" content="noindex, nofollow" />

      <div className="page-header">
        <div className="container">
          <span className="badge badge-accent" style={{ marginBottom: 16 }}>MEMBER</span>
          <h1>회원</h1>
          <p>회원가입하고 특별한 혜택을 받으세요</p>
        </div>
      </div>

      <section style={{ padding: '0 0 40px' }}>
        <div className="container">
          <div style={{
            display: 'flex', justifyContent: 'center',
            gap: 'clamp(24px, 5vw, 48px)', flexWrap: 'wrap',
          }}>
            {[
              { icon: 'Gift', label: '신규 가입 할인' },
              { icon: 'MessageCircle', label: '커뮤니티 참여' },
              { icon: 'Gem', label: '등급별 혜택' },
            ].map((item, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div className="icon-glow" style={{ width: 48, height: 48, borderRadius: 12, color: 'var(--primary)' }}>
                  <Icon name={item.icon} size={22} />
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 8 }}>
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container"><div className="gradient-divider" style={{ marginTop: 0 }} /></div>

      <section className="section" style={{ paddingTop: 32 }}>
        <div className="container">
          {socialError && !socialLoading && (
            <p style={{ color: '#ff4444', textAlign: 'center', marginBottom: 16, fontSize: '0.9rem' }}>
              {socialError}
            </p>
          )}
          <LoginForm />
        </div>
      </section>

      {socialLoading && <SocialLoginOverlay />}
    </div>
  );
}

function SocialLoginOverlay() {
  const [stage, setStage] = useState(0);
  const messages = ['인증 정보 확인 중...', '계정 연결 중...', '거의 다 됐습니다...'];
  useEffect(() => {
    const interval = setInterval(() => {
      setStage(s => Math.min(s + 1, messages.length - 1));
    }, 1500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      background: 'rgba(10, 22, 40, 0.92)', backdropFilter: 'blur(6px)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 20,
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        border: '4px solid rgba(74, 159, 255, 0.2)',
        borderTopColor: 'var(--primary)',
        animation: 'spin 0.8s linear infinite',
      }} />
      <div style={{ textAlign: 'center' }}>
        <p style={{ color: 'var(--text)', fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>
          {messages[stage]}
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
          잠시만 기다려주세요 (최대 5초)
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
